export const OFFLINE_DB_NAME = "ebooks-reader-offline"
export const OFFLINE_DB_VERSION = 1
export const OFFLINE_CHUNK_SIZE = 8 * 1024 * 1024
export const OFFLINE_CACHE_PREFIX = "ebooks-reader-offline-reader-"

const PDF_STORE = "pdfs"
const STATE_STORE = "state"
const ROOT_DIRECTORY = "ebooks-reader-offline"

function activeEditionKey(userId, bookId) {
  return `activeEdition:${userId}:${bookId}`
}

export function offlinePdfSupported({ storage = globalThis.navigator?.storage, indexedDB = globalThis.indexedDB, write = false } = {}) {
  // Reading existing copies does not require the writable stream API.
  return Boolean(indexedDB && typeof storage?.getDirectory === "function" &&
    (!write || typeof globalThis.FileSystemFileHandle?.prototype?.createWritable === "function"))
}

export function offlinePdfKey({ userId, editionId, checksum }) {
  return `${userId}:${editionId}:${checksum}`
}

export function chunkPlan(byteSize, chunkSize = OFFLINE_CHUNK_SIZE) {
  if (!Number.isSafeInteger(byteSize) || byteSize <= 0) throw new Error("Invalid PDF byte size")
  if (!Number.isSafeInteger(chunkSize) || chunkSize <= 0) throw new Error("Invalid PDF chunk size")

  const chunks = []
  for (let begin = 0, index = 0; begin < byteSize; begin += chunkSize, index += 1) {
    chunks.push({ index, begin, end: Math.min(begin + chunkSize, byteSize) })
  }
  return chunks
}

export function expectedChunkSize(record, index) {
  const chunk = chunkPlan(record.byteSize, record.chunkSize || OFFLINE_CHUNK_SIZE)[index]
  return chunk ? chunk.end - chunk.begin : null
}

export function offlineCopyExpired(record, now = Date.now()) {
  if (!record?.expiresAt) return false

  const expiration = Date.parse(record.expiresAt)
  return !Number.isFinite(expiration) || expiration <= now
}

export async function readAcrossChunks({ begin, end, byteSize, chunkSize = OFFLINE_CHUNK_SIZE, readChunk }) {
  if (!Number.isSafeInteger(begin) || !Number.isSafeInteger(end) || begin < 0 || end <= begin || end > byteSize) {
    throw new RangeError("Invalid offline PDF byte range")
  }

  const output = new Uint8Array(end - begin)
  const firstIndex = Math.floor(begin / chunkSize)
  const lastIndex = Math.floor((end - 1) / chunkSize)

  for (let index = firstIndex; index <= lastIndex; index += 1) {
    const chunk = new Uint8Array(await readChunk(index))
    const chunkBegin = index * chunkSize
    const sourceBegin = Math.max(begin, chunkBegin) - chunkBegin
    const sourceEnd = Math.min(end, chunkBegin + chunk.byteLength) - chunkBegin
    if (sourceEnd <= sourceBegin) throw new Error("Offline PDF chunk is incomplete")

    output.set(chunk.subarray(sourceBegin, sourceEnd), Math.max(begin, chunkBegin) - begin)
  }

  return output
}

export class OfflinePdfStore {
  constructor({ indexedDB = globalThis.indexedDB, storage = globalThis.navigator?.storage, caches = globalThis.caches } = {}) {
    this.indexedDB = indexedDB
    this.storage = storage
    this.caches = caches
  }

  supported({ write = false } = {}) {
    return offlinePdfSupported({ storage: this.storage, indexedDB: this.indexedDB, write })
  }

  async get(key) {
    return this.request(PDF_STORE, "readonly", (store) => store.get(key))
  }

  async put(record) {
    await this.request(PDF_STORE, "readwrite", (store) => {
      const request = store.get(record.key)
      request.addEventListener("success", () => {
        // A download may hold a stale record while another client discovers consent.
        if (request.result?.legalAcceptanceRequired) {
          record.legalAcceptanceRequired = request.result.legalAcceptanceRequired
          record.legalAcceptanceRevision = request.result.legalAcceptanceRevision
        }
        store.put(record)
      })
      return request
    })
    return record
  }

  async putIfActive(record, options = {}) {
    return this.changeActiveRecord(record, (storedRecord) => {
      const pendingProgress = storedRecord.pendingProgress
      const legal = Object.hasOwn(options, "legalAcceptanceRequired") ? options : storedRecord
      // Only a request started after the latest pause may clear that pause.
      const reauthorized = Object.hasOwn(options, "reauthorizedLegalAcceptanceRevision") &&
        (storedRecord.legalAcceptanceRevision ?? null) === options.reauthorizedLegalAcceptanceRevision
      return {
        ...record,
        ready: storedRecord.ready,
        legalAcceptanceRequired: reauthorized ? null : legal.legalAcceptanceRequired,
        legalAcceptanceRevision: reauthorized ? null : legal.legalAcceptanceRevision,
        progress: pendingProgress ? storedRecord.progress : record.progress,
        pendingProgress
      }
    })
  }

  async saveProgressIfActive(record, progress, { pending = false } = {}) {
    return this.changeActiveRecord(record, (storedRecord) => ({
      ...storedRecord,
      progress,
      pendingProgress: pending ? progress : storedRecord.pendingProgress,
      updatedAt: new Date().toISOString()
    }))
  }

  async acknowledgePendingProgress(record, progress) {
    return this.changeActiveRecord(record, (storedRecord) => {
      if (JSON.stringify(storedRecord.pendingProgress) !== JSON.stringify(progress)) return null

      return {
        ...storedRecord,
        progress,
        pendingProgress: null,
        updatedAt: new Date().toISOString()
      }
    }, { unchangedStatus: "pending", savedStatus: "acknowledged" })
  }

  async activeRecord(record) {
    return this.changeActiveRecord(record, () => null, { unchangedStatus: "active" })
  }

  async changeActiveRecord(record, change, { unchangedStatus = "unchanged", savedStatus = "saved" } = {}) {
    const bookId = record.book?.id
    if (bookId === undefined || bookId === null) return { status: "missing", record: null }

    const database = await this.database()
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STATE_STORE, PDF_STORE], "readwrite")
      const stateStore = transaction.objectStore(STATE_STORE)
      const pdfStore = transaction.objectStore(PDF_STORE)
      const stateRequest = stateStore.get(activeEditionKey(record.userId, bookId))
      const pdfRequest = pdfStore.get(record.key)
      let stateLoaded = false
      let recordLoaded = false
      let result = { status: "missing", record: null }

      const applyChange = () => {
        if (!stateLoaded || !recordLoaded) return

        const storedRecord = pdfRequest.result
        if (String(stateRequest.result?.value?.editionId) !== String(record.editionId)) {
          result = { status: "superseded", record: storedRecord || null }
          return
        }
        if (!storedRecord) return

        const updatedRecord = change(structuredClone(storedRecord))
        if (!updatedRecord) {
          result = { status: unchangedStatus, record: storedRecord }
          return
        }

        pdfStore.put(updatedRecord)
        result = { status: savedStatus, record: updatedRecord }
      }

      stateRequest.onsuccess = () => {
        stateLoaded = true
        applyChange()
      }
      pdfRequest.onsuccess = () => {
        recordLoaded = true
        applyChange()
      }
      stateRequest.onerror = () => reject(stateRequest.error)
      pdfRequest.onerror = () => reject(pdfRequest.error)
      transaction.oncomplete = () => resolve(result)
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  }

  async selectActiveEdition({ userId, bookId, editionId }) {
    const database = await this.database()
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STATE_STORE, PDF_STORE], "readwrite")
      const stateStore = transaction.objectStore(STATE_STORE)
      const pdfStore = transaction.objectStore(PDF_STORE)
      const request = pdfStore.getAll()
      let staleRecords = []

      request.onsuccess = () => {
        staleRecords = request.result.filter((record) => (
          String(record.userId) === String(userId) &&
          record.book?.id === bookId &&
          record.editionId !== editionId
        ))
        const updatedAt = new Date().toISOString()
        for (const record of staleRecords) {
          pdfStore.put({ ...record, ready: false, pendingProgress: null, updatedAt })
        }
        stateStore.put({
          key: activeEditionKey(userId, bookId),
          value: { editionId }
        })
      }
      request.onerror = () => reject(request.error)
      transaction.oncomplete = () => resolve(staleRecords)
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  }

  async markReadyIfActive(record) {
    const database = await this.database()
    return new Promise((resolve, reject) => {
      const transaction = database.transaction([STATE_STORE, PDF_STORE], "readwrite")
      const stateStore = transaction.objectStore(STATE_STORE)
      const pdfStore = transaction.objectStore(PDF_STORE)
      const request = stateStore.get(activeEditionKey(record.userId, record.book.id))
      let result

      request.onsuccess = () => {
        const active = request.result?.value?.editionId === record.editionId
        const pdfRequest = pdfStore.get(record.key)
        pdfRequest.onsuccess = () => {
          const storedRecord = {
            ...record,
            legalAcceptanceRequired: pdfRequest.result?.legalAcceptanceRequired,
            legalAcceptanceRevision: pdfRequest.result?.legalAcceptanceRevision,
            ready: active,
            pendingProgress: active ? record.pendingProgress : null,
            updatedAt: new Date().toISOString()
          }
          pdfStore.put(storedRecord)
          result = { active, record: storedRecord }
        }
        pdfRequest.onerror = () => reject(pdfRequest.error)
      }
      request.onerror = () => reject(request.error)
      transaction.oncomplete = () => resolve(result)
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  }

  async list(userId = null) {
    const records = await this.request(PDF_STORE, "readonly", (store) => store.getAll())
    return userId === null ? records : records.filter((record) => String(record.userId) === String(userId))
  }

  async setActiveUser(userId) {
    return this.request(STATE_STORE, "readwrite", (store) => store.put({ key: "activeUser", value: userId }))
  }

  async activeUser() {
    return (await this.request(STATE_STORE, "readonly", (store) => store.get("activeUser")))?.value ?? null
  }

  async setPendingUserCleanup(userId) {
    return this.request(STATE_STORE, "readwrite", (store) => store.put({ key: "pendingUserCleanup", value: userId }))
  }

  async pendingUserCleanup() {
    return (await this.request(STATE_STORE, "readonly", (store) => store.get("pendingUserCleanup")))?.value ?? null
  }

  async clearPendingUserCleanup() {
    return this.request(STATE_STORE, "readwrite", (store) => store.delete("pendingUserCleanup"))
  }

  async writeChunk(record, index, bytes) {
    const directory = await this.pdfDirectory(record, { create: true })
    const handle = await directory.getFileHandle(this.chunkFilename(index), { create: true })
    const writable = await handle.createWritable()
    try {
      await writable.write(bytes)
      await writable.close()
    } catch (error) {
      await writable.abort().catch(() => {})
      throw error
    }
  }

  async readRange(record, begin, end) {
    const directory = await this.pdfDirectory(record)
    return readAcrossChunks({
      begin,
      end,
      byteSize: record.byteSize,
      chunkSize: record.chunkSize,
      readChunk: async (index) => {
        const handle = await directory.getFileHandle(this.chunkFilename(index))
        const file = await handle.getFile()
        const expected = expectedChunkSize(record, index)
        if (file.size !== expected) throw new Error("Offline PDF chunk has an unexpected size")
        return file.arrayBuffer()
      }
    })
  }

  async completedChunks(record) {
    const completed = []
    let directory
    try {
      directory = await this.pdfDirectory(record)
    } catch (error) {
      if (error.name === "NotFoundError") return completed
      throw error
    }

    for (const chunk of chunkPlan(record.byteSize, record.chunkSize)) {
      try {
        const handle = await directory.getFileHandle(this.chunkFilename(chunk.index))
        const file = await handle.getFile()
        if (file.size === chunk.end - chunk.begin) completed.push(chunk.index)
      } catch (error) {
        if (error.name !== "NotFoundError") throw error
      }
    }
    return completed
  }

  async verifyComplete(record) {
    const completed = await this.completedChunks(record)
    return completed.length === chunkPlan(record.byteSize, record.chunkSize).length
  }

  async delete(record) {
    // Revoke local access before OPFS cleanup, which may fail and need a retry.
    // This prevents a cached reader from treating leftover chunks as usable.
    const disabledRecord = { ...record, ready: false, pendingProgress: null }
    await this.put(disabledRecord)
    await this.deleteCachedReader(disabledRecord)
    await this.deleteFiles(disabledRecord)
    await this.request(PDF_STORE, "readwrite", (store) => store.delete(record.key))
  }

  async deleteCachedReader(record) {
    if (!this.caches || !record.readerUrl) return

    if (!await this.caches.has(`${OFFLINE_CACHE_PREFIX}${record.userId}`)) return
    const cache = await this.caches.open(`${OFFLINE_CACHE_PREFIX}${record.userId}`)
    await cache.delete(record.readerUrl)
  }

  async deleteFiles(record) {
    try {
      const editionDirectory = await this.editionDirectory(record)
      await editionDirectory.removeEntry(this.safeName(record.checksum), { recursive: true })
    } catch (error) {
      if (error.name !== "NotFoundError") throw error
    }
  }

  async deleteOtherEditions(record) {
    const records = await this.list(record.userId)
    for (const candidate of records) {
      const sameBook = candidate.book?.id === record.book?.id
      const differentCopy = candidate.editionId !== record.editionId || candidate.checksum !== record.checksum
      if (sameBook && differentCopy) await this.delete(candidate)
    }
  }

  async deleteOtherEditionsForBook({ userId, bookId, editionId }) {
    const records = await this.list(userId)
    for (const candidate of records) {
      if (candidate.book?.id === bookId && candidate.editionId !== editionId) await this.delete(candidate)
    }
  }

  async clearUser(userId) {
    const records = await this.list(userId)
    for (const record of records) await this.delete(record)

    if (this.storage?.getDirectory) {
      try {
        const root = await this.offlineRoot()
        await root.removeEntry(`user-${this.safeName(userId)}`, { recursive: true })
      } catch (error) {
        if (error.name !== "NotFoundError") throw error
      }
    }

    if (String(await this.activeUser()) === String(userId)) {
      await this.request(STATE_STORE, "readwrite", (store) => store.delete("activeUser"))
    }
    await this.clearActiveEditionSelections(userId)
    if (this.caches) await this.caches.delete(`${OFFLINE_CACHE_PREFIX}${userId}`)
    await this.verifyUserCleared(userId)
  }

  async clearActiveEditionSelections(userId) {
    const prefix = `activeEdition:${userId}:`
    const database = await this.database()
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(STATE_STORE, "readwrite")
      const store = transaction.objectStore(STATE_STORE)
      const request = store.getAllKeys()

      request.onsuccess = () => {
        request.result.filter((key) => String(key).startsWith(prefix)).forEach((key) => store.delete(key))
      }
      request.onerror = () => reject(request.error)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
      transaction.onabort = () => reject(transaction.error)
    })
  }

  async clearAll() {
    if (this.storage?.getDirectory) {
      try {
        const root = await this.storage.getDirectory()
        await root.removeEntry(ROOT_DIRECTORY, { recursive: true })
      } catch (error) {
        if (error.name !== "NotFoundError") throw error
      }
    }

    if (this.caches) {
      const names = await this.caches.keys()
      await Promise.all(names.filter((name) => name.startsWith(OFFLINE_CACHE_PREFIX)).map((name) => this.caches.delete(name)))
    }

    await this.closeDatabase()
    await new Promise((resolve, reject) => {
      const request = this.indexedDB.deleteDatabase(OFFLINE_DB_NAME)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
      request.onblocked = () => reject(new Error("Offline database deletion is blocked"))
    })
  }

  async verifyUserCleared(userId) {
    if ((await this.list(userId)).length) throw new Error("Offline metadata deletion could not be verified")
    const stateKeys = await this.request(STATE_STORE, "readonly", (store) => store.getAllKeys())
    if (stateKeys.some((key) => String(key).startsWith(`activeEdition:${userId}:`))) {
      throw new Error("Offline edition selection deletion could not be verified")
    }

    if (this.storage?.getDirectory) {
      try {
        const root = await this.offlineRoot()
        await root.getDirectoryHandle(`user-${this.safeName(userId)}`)
        throw new Error("Offline file deletion could not be verified")
      } catch (error) {
        if (error.name !== "NotFoundError") throw error
      }
    }

    if (this.caches && (await this.caches.keys()).includes(`${OFFLINE_CACHE_PREFIX}${userId}`)) {
      throw new Error("Offline page deletion could not be verified")
    }
  }

  async pdfDirectory(record, { create = false } = {}) {
    const edition = await this.editionDirectory(record, { create })
    return edition.getDirectoryHandle(this.safeName(record.checksum), { create })
  }

  async editionDirectory(record, { create = false } = {}) {
    const root = await this.offlineRoot({ create })
    const user = await root.getDirectoryHandle(`user-${this.safeName(record.userId)}`, { create })
    return user.getDirectoryHandle(`edition-${this.safeName(record.editionId)}`, { create })
  }

  async offlineRoot({ create = false } = {}) {
    const root = await this.storage.getDirectory()
    return root.getDirectoryHandle(ROOT_DIRECTORY, { create })
  }

  chunkFilename(index) {
    return `chunk-${index}`
  }

  safeName(value) {
    return String(value).replace(/[^a-zA-Z0-9._-]/g, "_")
  }

  async request(storeName, mode, buildRequest) {
    const database = await this.database()
    return new Promise((resolve, reject) => {
      const transaction = database.transaction(storeName, mode)
      const request = buildRequest(transaction.objectStore(storeName))
      let settled = false
      let result

      const finish = (callback, value) => {
        if (settled) return
        settled = true
        callback(value)
      }

      request.onsuccess = () => {
        result = request.result
        if (mode === "readonly") finish(resolve, result)
      }
      request.onerror = () => finish(reject, request.error)
      transaction.oncomplete = () => finish(resolve, result)
      transaction.onerror = () => finish(reject, transaction.error)
      transaction.onabort = () => finish(reject, transaction.error)
    })
  }

  async database() {
    if (this.databasePromise) return this.databasePromise
    if (!this.indexedDB) throw new Error("IndexedDB is unavailable")

    this.databasePromise = new Promise((resolve, reject) => {
      const request = this.indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION)
      request.onupgradeneeded = () => {
        const database = request.result
        if (!database.objectStoreNames.contains(PDF_STORE)) database.createObjectStore(PDF_STORE, { keyPath: "key" })
        if (!database.objectStoreNames.contains(STATE_STORE)) database.createObjectStore(STATE_STORE, { keyPath: "key" })
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
      request.onblocked = () => reject(new Error("Offline database is blocked"))
    })
    return this.databasePromise
  }

  async closeDatabase() {
    if (!this.databasePromise) return
    const database = await this.databasePromise
    database.close()
    this.databasePromise = null
  }
}
